import { PageHero } from "@/components/site/PageHero";
import { FadeUp, MaskLines, Em, Eyebrow } from "@/components/site/Motion";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { POSITIONS } from "@/lib/site";
import { HeroCard } from "@/components/site/HeroCard";

const PROMISES = [
  { n: "01", t: "Someone replies inside the hour.", b: "Not an auto-reply. One of us, who has read what you sent.", cls: "lg:col-span-6" },
  { n: "02", t: "You know before we send.", b: "The client, the role and the rate, told to you first. Your profile never goes anywhere you haven't agreed to.", cls: "lg:col-span-5 lg:col-start-8 lg:mt-24" },
  { n: "03", t: "A 'no' is still an answer.", b: "If it's not a fit, we say so. Quickly. With a reason. Silence is not a hiring process.", cls: "lg:col-span-5 lg:col-start-2" },
  { n: "04", t: "We remember you.", b: "Missed this one? You're first in line for the next one that fits. That's what a bench is for.", cls: "lg:col-span-6 lg:col-start-7 lg:-mt-10" },
];

const Promises = () => (
  <section className="grain grain-dark relative bg-[#0E0E10] px-5 py-24 text-[#F1ECE2] sm:px-8 lg:py-36" data-testid="promises-section">
    <div className="relative mx-auto max-w-[1400px]">
      <Eyebrow className="text-[#D4FF3D]">what you can hold us to</Eyebrow>
      <MaskLines as="h2" lines={["talent gets", <>the same clock. <Em className="text-[#FF4D1A]">always.</Em></>]}
        className="font-display mt-6 text-4xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-6xl" />
      <div className="mt-16 grid gap-14 lg:grid-cols-12">
        {PROMISES.map((p, i) => (
          <FadeUp key={p.n} delay={i * 0.06} className={p.cls} data-testid={`promise-${p.n}`}>
            <span className="font-display stroke-text text-8xl font-bold leading-none text-[#FF4D1A]">{p.n}</span>
            <h3 className="font-display mt-4 text-3xl font-bold tracking-tight">{p.t}</h3>
            <p className="mt-3 max-w-md leading-relaxed text-[#d6d0c4]">{p.b}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Bench = () => (
  <section className="px-5 py-24 sm:px-8 lg:py-32" data-testid="bench-section">
    <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Eyebrow className="text-[#c2380f]">not a job board</Eyebrow>
        <MaskLines as="h2" lines={["we don't post", <>a hundred listings. <Em>we call.</Em></>]} className="font-display mt-6 text-4xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-5xl" />
        <p className="mt-6 max-w-md leading-relaxed text-[#3d3a35]">We keep a short list of people we'd put our name behind, and when a role opens, they hear first. Send your profile once. Get the call when it counts.</p>
      </div>
      <div className="flex flex-wrap content-start gap-2.5 lg:col-span-6 lg:col-start-7">
        {POSITIONS.flatMap((p) => p.tags).map((t, i) => (
          <FadeUp key={t} delay={i * 0.02} y={12}>
            <span className={`inline-block rounded-full border-2 border-[#0E0E10] px-4 py-2 text-sm font-semibold transition-colors duration-300 hover:bg-[#0E0E10] hover:text-[#F1ECE2] ${i % 7 === 0 ? "bg-[#D4FF3D]" : ""}`}>{t}</span>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const FIELDS = [
  { name: "name", label: "full name", required: true },
  { name: "email", label: "email", type: "email", required: true },
  { name: "phone", label: "phone" },
  { name: "role", label: "what you do best", placeholder: "e.g. Azure DevOps engineer", required: true },
  { name: "experience", label: "experience", type: "select", options: ["0–2 yrs", "3–5 yrs", "6–9 yrs", "10–14 yrs", "15+ yrs"], required: true },
  { name: "profile_url", label: "linkedin / cv link", placeholder: "https://" },
  { name: "message", label: "the bit your cv doesn't say", type: "textarea", span: 2 },
];

export default function Talent() {
  return (
    <div data-testid="talent-page">
      <PageHero testid="talent-hero" eyebrow="for engineers & specialists" lines={["you've been ready", "for a while.", <Em className="text-[#2234F5]">we noticed.</Em>]}
        aside={<HeroCard big="#01" unit="your number" tone="cobalt" tilt="sm:rotate-[3deg]" caption="First in line when a role fits. A reply from one of us inside the hour, every time." testid="talent-hero-card" />}
        lead="The skill is there. The hours are in. What's missing is the right room and someone willing to open the door. That part's on us." />
      <Promises />
      <Bench />
      <section className="bg-[#2234F5] px-5 py-24 text-white sm:px-8 lg:py-32" data-testid="talent-form-section">
        <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Eyebrow className="text-[#D4FF3D]">send your profile</Eyebrow>
            <MaskLines as="h2" lines={["step up.", <Em>we'll take it from here.</Em>]} className="font-display mt-6 text-4xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-5xl" />
            <p className="mt-6 text-white/90">Two minutes now. A reply from one of us inside the hour.</p>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <EnquiryForm kind="talent" fields={FIELDS} regionLabel="where do you want to work?" cta="put me in" testid="talent-form" />
          </div>
        </div>
      </section>
    </div>
  );
}
